import { MatDialog } from '@angular/material/dialog';
import { EventClickArg, EventInput, EventMountArg } from '@fullcalendar/core';
import { TimeOffEventDialogComponent } from '../components/shared/time-off-event-dialog/time-off-event-dialog.component';
import { HolidayDto } from '../models/holiday.models';
import { ResourceTimeOffEntryDto } from '../models/resource.models';
import { TimeOffEntry } from '../models/time-off.models';

/**
 * Status-based color mapping for FullCalendar time-off events.
 * Uses a light tinted background with a solid colored border/text to match the time-off-calendar component.
 */
const STATUS_COLOR_MAP: Record<
  string,
  { bg: string; border: string; text: string }
> = {
  APPROVED: {
    bg: 'color-mix(in srgb, #729a4c 15%, white)',
    border: '#729a4c',
    text: '#729a4c',
  },
  PENDING: {
    bg: 'color-mix(in srgb, #d68232 15%, white)',
    border: '#d68232',
    text: '#d68232',
  },
  DENIED: {
    bg: 'color-mix(in srgb, #9f3223 15%, white)',
    border: '#9f3223',
    text: '#9f3223',
  },
  CANCELLED: {
    bg: 'color-mix(in srgb, #65666a 15%, white)',
    border: '#65666a',
    text: '#65666a',
  },
};

const DEFAULT_EVENT_COLORS = STATUS_COLOR_MAP['APPROVED'];

/**
 * Classifies an entry's carry-over as none / partial / full (PBI 5770 AC#3, PBI 6228 AC#3).
 * Single source of truth for the "all countable days on carry-over vs only some" rule, shared by
 * the calendar title suffix and the event dialog label so they can never disagree.
 * `count` is the number of carry-over days; `daysOff` is the countable-day total.
 */
export function carryOverKind(
  count: number,
  daysOff: number,
): 'none' | 'partial' | 'full' {
  if (count <= 0) return 'none';
  return count >= daysOff ? 'full' : 'partial';
}

/**
 * Builds the carry-over designation appended to a calendar event title (PBI 5770 AC#3, PBI 6228 AC#3).
 * A single request renders as one spanning bar, so the title conveys presence: all of the
 * entry's countable days on carry-over -> "(Carry-Over)"; only some -> "(Partial Carry-Over)".
 * The event dialog shows the exact per-day breakdown. `daysOff` is the countable-day total.
 */
export function carryOverSuffix(
  carryOverDays: string[] | undefined,
  daysOff: number,
): string {
  switch (carryOverKind(carryOverDays?.length ?? 0, daysOff)) {
    case 'full':
      return ' (Carry-Over)';
    case 'partial':
      return ' (Partial Carry-Over)';
    default:
      return '';
  }
}

/**
 * Maps time-off entries to FullCalendar EventInput objects with status-based coloring.
 */
export function mapTimeOffToEvents(
  entries: ResourceTimeOffEntryDto[],
): EventInput[] {
  return entries.map((entry) => {
    // FullCalendar end date for allDay events is exclusive, so add 1 day
    let endDate = entry.endDate;
    if (endDate) {
      const d = new Date(endDate + 'T00:00:00');
      d.setDate(d.getDate() + 1);
      endDate = d.toISOString().split('T')[0];
    }

    const statusKey = entry.statusCode?.toUpperCase() || 'APPROVED';
    const colors = STATUS_COLOR_MAP[statusKey] || DEFAULT_EVENT_COLORS;

    const classYearSuffix = entry.classYear ? ` (${entry.classYear})` : '';
    const coSuffix = carryOverSuffix(entry.carryOverDays, entry.daysOff);
    return {
      title: entry.typeName
        ? `${entry.fullName}${classYearSuffix} - ${entry.typeName}${coSuffix}`
        : `${entry.fullName}${classYearSuffix}${coSuffix}`,
      start: entry.startDate,
      end: endDate,
      allDay: true,
      backgroundColor: colors.bg,
      borderColor: colors.border,
      textColor: '#000000',
      classNames: [`fc-status-${statusKey.toLowerCase()}`],
      extendedProps: { entry },
    };
  });
}

const HIDDEN_STATUSES = new Set(['DENIED', 'CANCELLED']);

/**
 * Maps single-resource TimeOffEntry[] to FullCalendar EventInput[].
 * Filters out DENIED and CANCELLED entries.
 * Produces a ResourceTimeOffEntryDto-shaped object in extendedProps so the
 * event click dialog works consistently.
 */
export function mapTimeOffEntriesToEventInputs(
  entries: TimeOffEntry[],
  employeeId: string,
  employeeName: string,
  typeLookups: { id: number; code: string; name: string }[],
  classYear?: string,
  seniority?: string,
): EventInput[] {
  return entries
    .filter((entry) => {
      const code = (entry.statusCode || 'APPROVED').toUpperCase();
      return !HIDDEN_STATUSES.has(code);
    })
    .filter((entry) => entry.displayStartDate && entry.displayEndDate)
    .map((entry) => {
      // FullCalendar end date for allDay events is exclusive, so add 1 day
      let endDate = entry.displayEndDate!;
      const d = new Date(endDate + 'T00:00:00');
      d.setDate(d.getDate() + 1);
      endDate = d.toISOString().split('T')[0];

      const statusKey = (entry.statusCode || 'APPROVED').toUpperCase();
      const colors = STATUS_COLOR_MAP[statusKey] || DEFAULT_EVENT_COLORS;

      const typeName = entry.type
        ? String(entry.type)
        : typeLookups.find((t) => t.id === entry.typeLookupValueId)?.name ||
          'Unknown';
      const typeCode =
        entry.typeCode ||
        typeLookups.find((t) => t.id === entry.typeLookupValueId)?.code ||
        'OTHER';

      // Build a ResourceTimeOffEntryDto-compatible object for the event dialog
      const adaptedEntry: ResourceTimeOffEntryDto = {
        employeeId,
        firstName: employeeName.split(' ')[0] || '',
        lastName: employeeName.split(' ').slice(1).join(' ') || '',
        fullName: employeeName,
        timeOffRequestId: entry.id,
        typeName,
        typeCode,
        statusName: statusKey.charAt(0) + statusKey.slice(1).toLowerCase(),
        statusCode: statusKey,
        startDate: entry.displayStartDate!,
        endDate: entry.displayEndDate!,
        daysOff: entry.daysOff,
        days: entry.days || [],
        carryOverDays: entry.carryOverDays,
        practiceAreas: [],
        classYear,
        seniority,
      };

      const classYearSuffix = classYear ? ` (${classYear})` : '';
      const coSuffix = carryOverSuffix(entry.carryOverDays, entry.daysOff);
      return {
        title: `${employeeName}${classYearSuffix} - ${typeName}${coSuffix}`,
        start: entry.displayStartDate || undefined,
        end: endDate,
        allDay: true,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: '#000000',
        classNames: [`fc-status-${statusKey.toLowerCase()}`],
        extendedProps: { entry: adaptedEntry },
      };
    });
}

const HOLIDAY_COLOR = '#005da8'; // --lapis-lazuli

/**
 * Maps HolidayDto[] from the API to FullCalendar EventInput[].
 * Parses the MM/dd/yyyy date format from the API into ISO dates.
 */
export function mapHolidaysToEventInputs(holidays: HolidayDto[]): EventInput[] {
  return holidays.map((holiday) => {
    // Parse MM/dd/yyyy → yyyy-MM-dd
    const [month, day, year] = holiday.date.split('/');
    const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

    return {
      title: holiday.title,
      start: isoDate,
      allDay: true,
      backgroundColor: `color-mix(in srgb, ${HOLIDAY_COLOR} 15%, white)`,
      borderColor: HOLIDAY_COLOR,
      textColor: '#000000',
      classNames: ['fc-holiday'],
      order: -1,
      extendedProps: { isHoliday: true },
    };
  });
}

/**
 * FullCalendar eventDidMount handler:
 * - Tags each segment with a data-event-id for cross-segment hover
 * - Appends "(continued)" in italics on wrapped segments
 */
export function timeOffEventDidMount(info: EventMountArg): void {
  const entry = info.event.extendedProps['entry'] as ResourceTimeOffEntryDto;

  // Tag each segment with the request ID so we can find all segments of the same event
  if (entry?.timeOffRequestId) {
    info.el.setAttribute('data-event-id', String(entry.timeOffRequestId));
  }

  // Append "(continued)" in italics for wrapped event segments
  if (!info.el.classList.contains('fc-event-start')) {
    const titleEl = info.el.querySelector('.fc-event-title');
    if (titleEl) {
      const suffix = document.createElement('span');
      suffix.style.fontStyle = 'italic';
      suffix.style.opacity = '0.7';
      suffix.textContent = ' (continued)';
      titleEl.appendChild(suffix);
    }
  }
}

/**
 * FullCalendar eventMouseEnter handler:
 * Highlights all segments of the same multi-week event on hover.
 */
export function timeOffEventMouseEnter(info: {
  event: { extendedProps: Record<string, unknown> };
}): void {
  const entry = info.event.extendedProps['entry'] as ResourceTimeOffEntryDto;
  if (entry?.timeOffRequestId) {
    const segments = document.querySelectorAll(
      `[data-event-id="${entry.timeOffRequestId}"]`,
    );
    segments.forEach((el) => el.classList.add('fc-event-hover'));
  }
}

/**
 * FullCalendar eventMouseLeave handler:
 * Removes highlight from all segments of the same multi-week event.
 */
export function timeOffEventMouseLeave(info: {
  event: { extendedProps: Record<string, unknown> };
}): void {
  const entry = info.event.extendedProps['entry'] as ResourceTimeOffEntryDto;
  if (entry?.timeOffRequestId) {
    const segments = document.querySelectorAll(
      `[data-event-id="${entry.timeOffRequestId}"]`,
    );
    segments.forEach((el) => el.classList.remove('fc-event-hover'));
  }
}

/**
 * FullCalendar eventClick handler that opens the TimeOffEventDialogComponent.
 * Protects FullCalendar's "more" popover from being dismissed when the dialog closes.
 */
export function createTimeOffEventClickHandler(getDialog: () => MatDialog) {
  return (info: EventClickArg): void => {
    const entry = info.event.extendedProps['entry'] as ResourceTimeOffEntryDto;
    if (!entry) return;

    // Prevent the click from bubbling so FullCalendar's "more" popover stays open
    info.jsEvent.stopPropagation();

    // Remove focus from the event element so it doesn't stay highlighted
    (info.el as HTMLElement).blur();

    const dialogRef = getDialog().open(TimeOffEventDialogComponent, {
      width: '420px',
      data: entry,
      panelClass: 'time-off-event-dialog',
    });

    // Block mouse events during the dialog close animation to prevent
    // FullCalendar's popover from detecting the overlay teardown as an outside click
    dialogRef.beforeClosed().subscribe(() => {
      const blockEvent = (e: Event) => e.stopPropagation();
      const eventTypes = [
        'mousedown',
        'mouseup',
        'pointerdown',
        'pointerup',
        'click',
        'focusin',
      ];
      eventTypes.forEach((type) =>
        document.addEventListener(type, blockEvent, { capture: true }),
      );
      setTimeout(() => {
        eventTypes.forEach((type) =>
          document.removeEventListener(type, blockEvent, { capture: true }),
        );
      }, 500);
    });
  };
}
